import { useEffect, useState } from "react";
import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import {
  Organization,
  organizationCreateOrUpdateInternalSchema,
} from "../../../api/schemas/organization";
import { booleanOrUndefinedSchema } from "../../../api/schemas/shared";
import { OrganizationBizType } from "../../../domain/organization";
import Constants from "../../shared/constants";
import DrawerForm from "../../shared/form/Drawer";
import { Input } from "../../shared/form/Input";
import { Select } from "../../shared/form/Select";
import useMetriportToast from "../../shared/toast";
import { mapToOptions, captureAndDisplayError } from "../../shared/util";
import { createOrUpdateOrganization } from "../../../api/internal";

const formSchema = organizationCreateOrUpdateInternalSchema
  .omit({
    id: true,
    cqApproved: true,
    cqActive: true,
    cwApproved: true,
    cwActive: true,
    delegateOids: true,
  })
  .merge(
    z.object({
      cqApproved: booleanOrUndefinedSchema,
      cqActive: booleanOrUndefinedSchema,
      cwApproved: booleanOrUndefinedSchema,
      cwActive: booleanOrUndefinedSchema,
    })
  );
type FormData = z.infer<typeof formSchema>;

export default function OrganizationInternalForm({
  isOpen,
  onClose,
  onSubmit,
  cxId,
  currentOrg,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cxId: string, newOrganization: Organization) => void;
  cxId: string;
  currentOrg: Organization | undefined;
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useMetriportToast();

  const [isMapi, setIsMapi] = useState<boolean>(
    currentOrg ? currentOrg.businessType === OrganizationBizType.healthcareITVendor : false
  );

  const [isDelegate, setIsDelegate] = useState<boolean>(Boolean(currentOrg?.principalOid));

  const [delegateOids, setDelegateOids] = useState<{ id: string; value: string }[]>(
    (currentOrg?.delegateOids ?? []).map(oid => ({ id: crypto.randomUUID(), value: oid }))
  );

  useEffect(() => {
    if (isOpen) {
      setIsDelegate(Boolean(currentOrg?.principalOid));
      setDelegateOids(
        (currentOrg?.delegateOids ?? []).map(oid => ({ id: crypto.randomUUID(), value: oid }))
      );
    }
  }, [isOpen, currentOrg]);

  function addDelegateOid() {
    setDelegateOids([...delegateOids, { id: crypto.randomUUID(), value: "" }]);
  }

  function updateDelegateOid(id: string, value: string) {
    setDelegateOids(delegateOids.map(oid => (oid.id === id ? { ...oid, value } : oid)));
  }

  function removeDelegateOid(id: string) {
    setDelegateOids(delegateOids.filter(oid => oid.id !== id));
  }

  const createOrUpdate = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      let newOrganization: Organization;
      const filteredDelegateOids = isDelegate
        ? []
        : delegateOids.map(oid => oid.value.trim()).filter(value => value.length > 0);
      const adjustedParams = {
        cqActive: isMapi
          ? undefined
          : data.cqActive !== "undefined"
          ? data.cqActive === "true"
          : undefined,
        cwActive: isMapi
          ? undefined
          : data.cwActive !== "undefined"
          ? data.cwActive === "true"
          : undefined,
        cqApproved: isMapi
          ? undefined
          : data.cqApproved !== "undefined"
          ? data.cqApproved === "true"
          : undefined,
        cwApproved: isMapi
          ? undefined
          : data.cwApproved !== "undefined"
          ? data.cwApproved === "true"
          : undefined,
        principalOid: isDelegate ? data.principalOid : undefined,
        delegateOids: filteredDelegateOids,
        businessType: isMapi
          ? OrganizationBizType.healthcareITVendor
          : OrganizationBizType.healthcareProvider,
      };
      if (currentOrg) {
        newOrganization = await createOrUpdateOrganization(cxId, {
          ...{
            ...currentOrg,
            location: currentOrg.location,
          },
          ...data,
          ...adjustedParams,
        });
      } else {
        newOrganization = await createOrUpdateOrganization(cxId, {
          ...data,
          ...adjustedParams,
        });
      }
      toast.success({
        duration: 10_000,
        title: `Organization saved.${
          adjustedParams.cqApproved || adjustedParams.cwApproved
            ? ` You have an HIE Approved. Please reload the page to see the new HIE facilities. It may take more than 30 seconds.`
            : ""
        }`,
      });
      onSubmit(cxId, newOrganization);
    } catch (error) {
      // TODO Check for timeout and flag as warning (#1940)
      captureAndDisplayError({
        error,
        msg: "Failed to save organization",
        context: "createOrUpdate",
        captureMsg: `Component: OrganizationInternalForm - ${
          currentOrg ? "update" : "create"
        } failed`,
        toast,
        extras: {
          cxId,
          orgId: currentOrg?.id,
        },
      });
    }
    setIsSubmitting(false);
  };

  return (
    <DrawerForm<FormData>
      title={`${currentOrg ? "Edit" : "Create"} Organization`}
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onSubmit={createOrUpdate}
      onClose={onClose}
      resolver={zodResolver(formSchema)}
      defaultValues={{
        nameInMetriport: currentOrg?.name,
        shortcode: currentOrg?.shortcode,
        type: currentOrg?.type,
        location: currentOrg?.location,
        principalOid: currentOrg?.principalOid ?? undefined,
      }}
    >
      {({ register, reset, formState: { errors } }) => (
        <>
          <Heading as="h4" size="sm" mb={5}>
            Organization Business Type
          </Heading>
          <Select
            isRequired
            options={Constants.orgBizTypeOptions}
            value={isMapi ? "IT Vendor" : "Provider"}
            onChange={change => {
              setIsMapi(change.target.value === "IT Vendor");
              reset();
            }}
            mb={12}
            isDisabled={!!currentOrg}
          />
          <Heading as="h4" size="sm" mb={5}>
            Basic Info
          </Heading>
          <Input
            {...register("nameInMetriport")}
            isRequired
            label="Name in Metriport *"
            error={get(errors, "nameInMetriport")}
          />
          <Input
            {...register("shortcode")}
            label="Short code for ADTs"
            error={get(errors, "shortcode")}
          />
          <Input
            {...register("businessType")}
            isRequired
            isReadOnly
            label="Business Type"
            value={isMapi ? "healthcare_it_vendor" : "healthcare_provider"}
            error={get(errors, "businessType")}
          />
          <Select
            {...register("type")}
            isRequired
            label="Treatment Type *"
            options={mapToOptions(Constants.treatmentTypes)}
            placeholder="Select Type"
            error={get(errors, "type")}
          />
          <Select
            isRequired
            label="Principal or Delegate *"
            value={isDelegate ? "delegate" : "principal"}
            options={Constants.facilityOptions}
            onChange={change => {
              const newIsDelegate = change.target.value === "delegate";
              setIsDelegate(newIsDelegate);
            }}
          />
          {isDelegate && delegateOids.length > 0 && (
            <Text color="orange.500" fontSize="sm" mt={2}>
              ⚠️ Warning: If you save, the existing Delegate OIDs will be deleted.
            </Text>
          )}
          {!isDelegate && currentOrg?.principalOid && (
            <Text color="orange.500" fontSize="sm" mt={2}>
              ⚠️ Warning: If you save, the existing Principal OID will be deleted.
            </Text>
          )}
          {isDelegate && (
            <Input
              {...register("principalOid")}
              isRequired
              label="Principal OID *"
              error={get(errors, "principalOid")}
            />
          )}
          {!isDelegate && (
            <Box mt={5}>
              <Box whiteSpace="nowrap" mb={2}>
                Delegate OIDs
              </Box>
              {delegateOids.map(oid => (
                <Flex key={oid.id} gap={2} mb={2} alignItems="center">
                  <Input
                    value={oid.value}
                    onChange={e => updateDelegateOid(oid.id, e.target.value)}
                    placeholder="e.g. 2.16.840.1.113883.3.xxx"
                    label=""
                  />
                  <Button
                    aria-label="Remove OID"
                    onClick={() => removeDelegateOid(oid.id)}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                  >
                    <CloseIcon boxSize={3} />
                  </Button>
                </Flex>
              ))}
              <Button size="sm" onClick={addDelegateOid} variant="outline" leftIcon={<AddIcon />}>
                Add Delegate OID
              </Button>
            </Box>
          )}
          <Box hidden={isMapi}>
            <Select
              {...register("cqApproved")}
              label="CQ Approved"
              defaultValue={
                currentOrg?.cqApproved !== undefined && currentOrg?.cqApproved !== null
                  ? `${currentOrg?.cqApproved}`
                  : "false"
              }
              options={Constants.booleanAndUndefinedOptions}
              error={get(errors, "cqApproved")}
            />
            <Select
              {...register("cqActive")}
              label="CQ Active"
              defaultValue={
                currentOrg?.cqActive !== undefined && currentOrg?.cqActive !== null
                  ? `${currentOrg?.cqActive}`
                  : "false"
              }
              options={Constants.booleanAndUndefinedOptions}
              error={get(errors, "cqActive")}
            />
            <Select
              {...register("cwApproved")}
              label="CW Approved"
              defaultValue={
                currentOrg?.cwApproved !== undefined && currentOrg?.cwApproved !== null
                  ? `${currentOrg?.cwApproved}`
                  : "false"
              }
              options={Constants.booleanAndUndefinedOptions}
              error={get(errors, "cwApproved")}
            />
            <Select
              {...register("cwActive")}
              label="CW Active"
              defaultValue={
                currentOrg?.cwActive !== undefined && currentOrg?.cwActive !== null
                  ? `${currentOrg?.cwActive}`
                  : "false"
              }
              options={Constants.booleanAndUndefinedOptions}
              error={get(errors, "cwActive")}
            />
          </Box>
          <Heading as="h4" size="sm" pt={50}>
            Address
          </Heading>
          <Input
            {...register("location.addressLine1")}
            isRequired
            label="Address Line 1 *"
            error={get(errors, "location.addressLine1")}
          />
          <Input
            {...register("location.addressLine2")}
            label="Address Line 2"
            error={get(errors, "location.addressLine2")}
          />
          <Input
            {...register("location.city")}
            isRequired
            label="City *"
            error={get(errors, "location.city")}
          />
          <Select
            {...register("location.state")}
            isRequired
            label="State *"
            options={Constants.usStatesForAddress}
            placeholder="Select State"
            error={get(errors, "location.state")}
          />
          <Input
            {...register("location.zip")}
            isRequired
            label="Zip *"
            error={get(errors, "location.zip")}
          />
          <Input
            {...register("location.country")}
            isRequired
            isReadOnly
            color={"grey"}
            label="Country *"
            value={"USA"}
            error={get(errors, "country")}
          />
        </>
      )}
    </DrawerForm>
  );
}
